// Core Domain Types
// Generated from DataModel.sql core schema

import type { Practice } from "./practices";
import type { Professional } from "./professionals";

// Core Enums
/**
 * Four roles. `platform` and `super_admin` were retired into `admin`, which now holds
 * every tenant-level and money-moving authority; `support` is the same console without
 * those operations (the API enforces this via `HasAdminRole` vs `HasStaffRole`).
 */
export type UserType =
  | "admin"
  | "support"
  | "practice"
  | "professional"
  | "none";

export type RegisterUserType = Extract<UserType, "practice" | "professional">;

/** Internal ops roles — both share the admin console. */
export const STAFF_USER_TYPES = [
  "admin",
  "support"
] as const satisfies readonly UserType[];

export function isStaffUserType(role: string): boolean {
  return (STAFF_USER_TYPES as readonly string[]).includes(role);
}

export type UserStatus =
  | "pending"
  | "active"
  | "dormant"
  | "suspended"
  | "banned";

export type UserDocumentType =
  | "license"
  | "tax_id"
  | "certificate"
  | "resume"
  | "shift"
  | "application"
  | "profile_photo"
  | "practice_photo"
  | "media"
  | "misc";

export type VerificationStatus =
  | "pending"
  | "under_review"
  | "approved"
  | "rejected"
  | "resubmitted";

export const VERIFICATION_STATUS_VALUES = [
  "pending",
  "under_review",
  "approved",
  "rejected",
  "resubmitted"
] as const satisfies readonly VerificationStatus[];

export function isVerificationStatus(
  value: string
): value is VerificationStatus {
  return (VERIFICATION_STATUS_VALUES as readonly string[]).includes(value);
}

/** Verification states that are not a final approve/reject decision. */
export const PENDING_VERIFICATION_STATUSES = [
  "pending",
  "under_review",
  "resubmitted"
] as const satisfies readonly VerificationStatus[];

export type PendingVerificationStatus =
  (typeof PENDING_VERIFICATION_STATUSES)[number];

export function isPendingVerificationStatus(
  status: string
): status is PendingVerificationStatus {
  return (PENDING_VERIFICATION_STATUSES as readonly string[]).includes(status);
}

export type WeekDay =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export type RecommendationRequestStatus = "latest" | "archived";

export type StripeAccountStatus =
  | "pending_verification"
  | "active"
  | "disabled";

export type BusinessType = "individual" | "company";

export type StripeExternalAccountType = "bank_account" | "card";

export type StripeExternalAccountStatus =
  | "new_account"
  | "validated"
  | "verified"
  | "verification_failed"
  | "errored";

export type BillingCycle = "monthly" | "yearly";

export type PricingPlanType = "flex" | "pro" | "network";

/** How a plan charges Tempii's per-shift fee. Plans that bundle it use "none". */
export type ShiftFeeModel = "none" | "commission" | "flat" | "per_post";

/** What a commission percentage is applied to. */
export type ShiftFeeBasis =
  | "engagement_value"
  | "single_engagement"
  | "post_value";

// Name Prefix Types
export const NamePrefixTypes = {
  Dr: "Dr.",
  Prof: "Prof.",
  Mr: "Mr.",
  Mrs: "Mrs.",
  Ms: "Ms."
} as const;

export type NamePrefixType =
  (typeof NamePrefixTypes)[keyof typeof NamePrefixTypes];

// Core Entities
export interface Tenant extends BaseEntity {
  title: string;
  stripeAccountId?: string;
  currencyId?: string;
  feePercentage: number;
  feeMin: Money;
  feeMax: Money;
  aiMatchingModelVersion: string;
  cancellationPenaltyPercentage: number;
  minHoursBeforeShiftCancellation: number;
  shiftReminderHours: number;
  maxTravelDistanceDefault: number;
  location: Address;
}

export interface Currency extends BaseEntity {
  id: string;
  tenantId: string;
  code: string;
  symbol: string;
  name: string;
  title: string;
  description?: string;
}

export interface StripeAccount extends BaseEntity {
  id: string;
  tenantId: string;
  stripeAccountStatus: StripeAccountStatus;
  accountId: string;
  accountType: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  capabilities: Record<string, unknown>;
  requirements: Record<string, unknown>;
  businessType?: BusinessType;
  businessProfile: Record<string, unknown>;
  settings: Record<string, unknown>;
  onboardingCompletedAt?: Date;
  lastStripeUpdate?: Date;
}

export interface StripeExternalAccount extends BaseEntity {
  id: string;
  stripeAccountId: string;
  externalAccountId: string;
  accountType: StripeExternalAccountType;
  isDefault: boolean;
  bankName?: string;
  last4?: string;
  routingNumberLast4?: string;
  currencyId: string;
  status?: StripeExternalAccountStatus;
}

export interface User extends BaseEntity {
  id: string;
  tenantId: string;
  idpObjectId: string;
  userType: UserType;
  status: UserStatus;
  namePrefix: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  fullName: string;
  designation?: string;
  phone?: string;
  phoneVerified: boolean;
  email?: string;
  emailVerified: boolean;
  profilePhotoId?: string;
  profilePhotoUrl?: string;
  verified: boolean;
  professionalId?: string;
  practiceId?: string;
  active: boolean;
  /**
   * When this user's session was last seen by the API.
   *
   * Written on the authenticated profile fetch that starts a session, at a 15-minute
   * resolution — see `LoginActivityWindow`. Absent for an account that has not signed
   * in since that became true.
   */
  lastLoginAt?: Date;
  /**
   * When the owner asked for the account to be erased.
   *
   * Set means the account is deactivated on purpose and awaiting erasure, so
   * reinstating it is refused server-side — see `UserModerationPolicy.CanReinstate`.
   */
  deletionRequestedAt?: Date;
}

export interface UserDocument extends BaseEntity {
  userId: string;
  documentType: UserDocumentType;
  uploadedAt: Date;
  fileName: string;
  fileSize: number;
  fileUrl: string;
  cdnFileUrl: string | null;
  fileMimeType: string | null;
  description: string | null;
  status: VerificationStatus;
  rejectionReason: string | null;
  version: number;
  reviewedAt: Date | null;
  reviewedBy: string | null;
}

export interface UserNotification extends BaseEntity {
  userId: string;
  notificationType: NotificationType;
  notificationScope: NotificationScope;
  notificationScopeId?: string;
  title: string;
  status: NotificationStatus;
}

export interface UserNotificationChannel extends BaseEntity {
  notificationId: string;
  channel: NotificationChannel;
  payload: Record<string, unknown>;
  status: NotificationStatus;
  isRead: boolean;
  readAt?: Date;
}

export interface PricingPlan extends BaseEntity {
  tenantId: string;
  title: string;
  description?: string;
  planType: PricingPlanType;
  billingCycle: BillingCycle;
  shiftFeeModel: ShiftFeeModel;
  shiftFeeBasis: ShiftFeeBasis;
  shiftFeePercentage?: number;
  shiftFeeFlatRateAmount?: number;
  shiftFeeFlatRateCurrency?: string;
  shiftFeeMinimumAmount?: number;
  shiftFeeMaximumAmount?: number;
  locationsCount: number;
  subscriptionFeesAmount: number;
  subscriptionFeesCurrency: string;
  iconPath: string;
  primaryColor: string;
  isLive: boolean;
  isDefault: boolean;
}

export interface NotificationBatch extends BaseEntity {
  tenantId: string;
  status: NotificationBatchStatus;
  title: string;
  description?: string;
}

export type NotificationBatchStatus =
  | "pending"
  | "ready"
  | "sent"
  | "payload_error"
  | "send_error";

export interface BlockedProfile extends BaseEntity {
  practiceId: string;
  professionalId: string;
}

// Request/Response Types
/** Client contract for admin tenant-configuration APIs — no `Tempii.Application` module yet; align when endpoints ship. */
export interface CreateTenantRequest {
  title: string;
  stripeAccountId?: string;
  currencyId?: string;
  feePercentage?: number;
  feeMinAmount?: number;
  feeMinCurrency?: string;
  feeMaxAmount?: number;
  feeMaxCurrency?: string;
  aiMatchingModelVersion?: string;
  cancellationPenaltyPercentage?: number;
  minHoursBeforeShiftCancellation?: number;
  shiftReminderHours?: number;
  maxTravelDistanceDefault?: number;
  location: Address;
}

/** @see client admin tenant list — verify OpenAPI when backend exists. */
export interface GetTenantsRequest {
  page?: number;
  limit?: number;
  status?: string;
}

export type GetTenantsResponse = Tenant[];

export type GetTenantRequest = string; // tenant id

export type GetTenantResponse = Tenant;

/** @see client admin tenant update — verify OpenAPI when backend exists. */
export interface UpdateTenantRequest extends Partial<CreateTenantRequest> {
  id: string;
}

export type UpdateTenantResponse = Tenant;

// Utility Types
export interface BaseEntity {
  id: string;
  createdBy: string;
  updatedBy?: string;
  createdAt: Date;
  updatedAt?: Date;
}

export type SortOrder = "asc" | "desc";

export interface PaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: SortOrder;
  searchTerm?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    firstItemIndex: number;
    lastItemIndex: number;
    sortBy?: string;
    sortOrder: SortOrder;
    searchTerm?: string;
  };
}

/**
 * Unified API response envelope returned by every versioned backend endpoint.
 * Mirrors `Tempii.Shared.ApiEnvelope<T>`. The frontend base query unwraps `data`
 * centrally, so feature code keeps working with the raw payload (`T`) directly.
 */
interface ApiEnvelopeBase {
  readonly status: number;
  readonly message: string;
  readonly meta: ApiMeta;
}

export interface ApiSuccessEnvelope<T> extends ApiEnvelopeBase {
  readonly success: true;
  readonly data: T;
  readonly error?: never;
}

export interface ApiFailureEnvelope extends ApiEnvelopeBase {
  readonly success: false;
  readonly data?: never;
  readonly error: ApiErrorBody;
}

export type ApiEnvelope<T> = ApiSuccessEnvelope<T> | ApiFailureEnvelope;

/** Structured error body. Mirrors `Tempii.Shared.ApiError`. */
export interface ApiErrorBody {
  readonly code: string;
  readonly message: string;
  readonly type: string;
  readonly details?: readonly ApiErrorBodyItem[];
}

/** A single sub-error (e.g. one validation failure). Mirrors `Tempii.Shared.ApiErrorItem`. */
export interface ApiErrorBodyItem {
  readonly code: string;
  readonly message: string;
}

/** Request-level metadata attached to every envelope. Mirrors `Tempii.Shared.ApiMeta`. */
export interface ApiMeta {
  readonly correlationId: string;
  readonly timestamp: string;
  readonly version: string;
}

// PUBLIC API Request & Response Types
export type HealthCheckRequest = Record<string, never>;

export interface HealthCheckResponse {
  status: "healthy" | "unhealthy";
  timestamp: Date;
  version: string;
  uptime: number;
  services: {
    database: "healthy" | "unhealthy";
    cache: "healthy" | "unhealthy";
    externalApis: "healthy" | "unhealthy";
  };
}

export type SystemInfoRequest = Record<string, never>;

export interface SystemInfoResponse {
  name: string;
  version: string;
  description: string;
  environment: string;
  features: string[];
  contact: {
    email: string;
    phone?: string;
    website?: string;
  };
  legal: {
    termsUrl: string;
    privacyUrl: string;
    supportUrl: string;
  };
}

// ADMIN API Request & Response Types — `Tempii.Application.Admin.*`

// Admin Stats API
/** 
 * Platform size at the end of one month, oldest first in `growthSeries`.
 *
 * Cumulative totals rather than new arrivals, so the last point equals the overview
 * counts. `period` is ISO year-month (`2026-08`); the label is the client's to make.
 *
 * @see Tempii.Application.Admin.GetAdminStats.GrowthPoint 
 * */
export interface AdminGrowthPoint {
  period: string;
  users: number;
  practices: number;
  professionals: number;
}

/** 
 * @see Tempii.Application.Admin.GetAdminStats.AdminStatsResponse 
 * */
export interface AdminStatsResponse {
  overview: {
    totalUsers: number;
    totalPractices: number;
    totalProfessionals: number;
    totalShifts: number;
    totalRevenue: number;
    activeUsers: number;
  };
  growth: {
    userGrowth: number;
    practiceGrowth: number;
    professionalGrowth: number;
    shiftGrowth: number;
    revenueGrowth: number;
  };
  growthSeries: AdminGrowthPoint[];
  performance: {
    shiftFillRate: number;
    averageTimeToFillHours: number;
    applicationConversionRate: number;
    satisfactionScore: number;
    shiftCompletionRate: number;
    averageRating: number;
  };
  financial: {
    monthlyRevenue: number;
    averageRevenuePerUser: number;
    averageRevenuePerPractice: number;
    averageRevenuePerProfessional: number;
    transactionVolume: number;
    averageShiftValue: number;
    transactionFees: number;
    premiumServices: number;
  };
  compliance: {
    pendingVerifications: number;
    documentComplianceRate: number;
    activeDisputes: number;
    resolvedDisputes: number;
  };
  quality: {
    averageProfessionalRating: number;
    averagePracticeRating: number;
    totalReviews: number;
    positiveReviews: number;
    negativeReviews: number;
    reviewCoverage: number;
    customerSatisfaction: number;
  };
  operational: {
    dailyActiveUsers: number;
    weeklyActiveUsers: number;
    monthlyActiveUsers: number;
    averageShiftsPerProfessional: number;
    averageShiftsPerPractice: number;
    repeatHireRate: number;
  };
}

/** @see Tempii.Application.Admin.GetAdminUser.GetAdminUserQuery */
export type AdminUserRequest = string; // user id

export type AdminUserResponse = User;

/** @see verify OpenAPI — admin user PATCH may not match this stub yet. */
export interface AdminUserUpdateRequest {
  id: string;
  status?: UserStatus;
  userType?: UserType;
  verified?: boolean;
  active?: boolean;
  emailVerified?: boolean;
  phoneVerified?: boolean;
}

export type AdminUserUpdateResponse = User;

// =====================
// Identity API Types
// =====================

export interface RegisterUserRequest {
  userId: string;
  email: string;
  firstName: string;
  namePrefix: string;
  middleName?: string;
  lastName: string;
  phone: string;
  designation?: string;
  addressCountry?: string;
  addressState?: string;
  addressCity?: string;
  addressStreet?: string;
  addressPostalCode?: string;
  addressLongitude?: number;
  addressLatitude?: number;
}

/** @see Tempii.Application.Users.GetUser.GetUserQuery (identity resolved server-side) */
export interface GetUserProfileRequest {
  userId: string;
}

/** @see Tempii.Application.Users.GetUser.GetUserResponse */
export interface GetUserProfileResponse extends User {
  userId: string;
  practice?: Practice;
  professional?: Professional;
  registrationRequired: boolean;
  registrationMessage: string;
}

/** @see Tempii.Application.Users.GetUserDocuments.GetUserDocumentsQuery */
export interface GetUserDocumentsRequest {
  userId: string;
}

/** @see Tempii.Application.Users.GetUserDocuments.GetUserDocumentsResponse */
export interface GetUserDocumentsResponse {
  documents: UserDocument[];
}

/** @see Tempii.Application.Users.UploadUserDocument.UploadUserDocumentCommand */
export interface UploadUserDocumentRequest {
  userId: string;
  formData: FormData;
}

/** @see Tempii.Application.Payloads.UserDocumentPayload */
export interface UploadUserDocumentResponse extends UserDocument { }

/** @see Tempii.Application.Users.DeleteUserDocument.DeleteUserDocumentCommand */
export interface DeleteUserDocumentRequest {
  userId: string;
  documentId: string;
}

/** @see Tempii.Application.Users.DeleteUserDocument (response wrapper) */
export interface DeleteUserDocumentResponse {
  value: string;
}

export interface Money {
  amount: number;
  currency?: string;
}

// Type definition for time slots
export interface TimeSlot {
  startTime: string;
  endTime: string;
}

export interface Address {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  latitude: number;
  longitude: number;
  formattedAddress?: string;
}

export interface IntegerRange {
  start: number;
  end: number;
}

export interface DateRange {
  start: Date;
  end: Date;
}

export interface UploadDocument {
  documentType: UserDocumentType;
  file: File;
  description?: string;
}

// Notification Types
/**
 * Mirrors `core.notification_type`. Keep it in step with `Tempii.Shared.NotificationType`:
 * a value missing here is not a type error anywhere, it just silently falls through
 * `notification-view.ts` to no icon and no action URL.
 *
 * `reminder_sent` and `message_received` are declared but nothing raises them yet.
 */
export type NotificationType =
  | "reminder_sent"
  | "profile_created"
  | "profile_updated"
  | "verification_in_progress"
  | "verification_updated"
  | "verification_completed"
  | "verification_revised"
  | "shift_posted"
  | "shift_post_updated"
  | "shift_post_applied"
  | "shift_application"
  | "professional_selected"
  | "shift_cancelled"
  | "referral_milestone"
  | "referral_completed"
  | "message_received"
  | "shift_checked_in"
  | "shift_completion_claimed"
  | "shift_completion_approved"
  | "shift_completion_rejected"
  | "payment_declared"
  | "payment_confirmed"
  | "fee_payment_failed"
  | "subscription_payment_failed"
  | "rating_received";

/** Mirrors `core.notification_scope`. */
export type NotificationScope =
  | "system"
  | "user"
  | "practice"
  | "professional"
  | "shift"
  | "shift_post"
  | "shift_post_slot"
  | "shift_applications"
  | "shift_application_posts";

export type NotificationChannel = "email" | "sms" | "push" | "web";

export type NotificationStatus =
  | "ready"
  | "sent"
  | "payload_error"
  | "send_error";

export interface NotificationItem {
  notificationId: string;
  title: string;
  message: string;
  type: NotificationType;
  scope: NotificationScope;
  scopeId?: string;
  status: NotificationStatus;
  createdAt: string;
  readAt?: string;
  data?: string;
  shiftId?: string;
  applicationId?: string;
}

/**
 * Shared list params for practice/professional notification queries.
 * @see Tempii.Application.Practices.GetPracticeNotifications.GetPracticeNotificationsQuery
 * @see Tempii.Application.Professionals.GetProfessionalNotifications.GetProfessionalNotificationsQuery
 */
export interface GetNotificationsRequest {
  entityId: string;
  page?: number;
  pageSize?: number;
  isRead?: boolean;
  searchTerm?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export type GetNotificationsResponse = PaginatedResponse<NotificationItem> & {
  unreadCount?: number;
};

/**
 * @see Tempii.Application.Users.MarkNotificationRead.MarkNotificationReadCommand
 * (fields `invalidateContext` are client-only for RTK cache)
 */
export interface MarkNotificationReadRequest {
  userId: string;
  notificationId: string;
  /** Refetch practice/professional notification queries after read */
  invalidateContext?: {
    kind: "practice" | "professional" | "user";
    entityId: string;
  };
}

/** @see Tempii.Application.Users.MarkAllNotificationsRead.MarkAllNotificationsReadCommand */
export interface MarkAllNotificationsReadRequest {
  userId: string;
  invalidateContext?: {
    kind: "practice" | "professional" | "user";
    entityId: string;
  };
}

/** @see Tempii.Application.Users.DeleteUserAccount.DeleteUserAccountCommand */
export interface DeleteUserAccountRequest {
  userId: string;
}
